import { LIFETIME_CLOUD_PROJECT_LIMIT, LIFETIME_INCLUDED_CREDITS } from "../pricing";
import { create } from "zustand";
import { requestBillingConfig } from "./config-request";
import { useAuthStore } from "../auth/store";
import { getSupabase, isSupabaseConfigured } from "../supabase/client";
import { useProjectStore } from "../projects/store";
import type { EntitlementSnapshot } from "../projects/types";

export type BillingProduct = "lifetime" | "installation_pack" | "monthly_subscription" | "pro_monthly" | "pro_yearly" | "ai_credits" | "pass";
export type Subscription = {
  userId: string;
  subscriptionId: string;
  status: "trialing" | "active" | "past_due" | "paused" | "canceled";
  trialEndsAt: string | null;
  currentPeriodEndsAt: string | null;
  nextBilledAt: string | null;
  scheduledCancelAt: string | null;
  active: boolean;
  productKey?: "monthly_subscription" | "pro_monthly" | "pro_yearly";
  billingInterval?: "month" | "year";
};
export type InstallationPack = {
  installationId: string;
  name: string;
  cloudProjectId: string | null;
  clientProjectIds: string[];
};

export type BillingConfig = {
  enabled: boolean;
  environment: "sandbox" | "production";
  priceId: string | null;
  displayPrice: string | null;
  productName: string;
  aiCredits?: { enabled: boolean; priceId: string | null; credits: number; amount: number; currency: string };
  installationPack?: { enabled: boolean; priceId: string | null; displayPrice: string | null; productName: string };
  subscription?: { enabled: boolean; priceId: string | null; displayPrice: string | null; productName: string; trialDays: 0 };
  pro?: { enabled: boolean; monthlyPriceId: string | null; yearlyPriceId: string | null; monthlyDisplayPrice: string | null; yearlyDisplayPrice: string | null; credits: number };
  /** The 12-month Crimp Pass. Dormant: it is sold only while the server says
   * `pass.enabled`, which it does not by default. */
  pass?: { enabled: boolean; priceId: string | null; displayPrice: string | null; productName: string; days: number; credits: number };
  /** Whether Crimp Lifetime may be bought. Absent means yes: only an explicit
   * `false` from the server retires it (then the Pass is sold if enabled). */
  lifetimeCheckoutEnabled?: boolean;
  /** Cloud projects a free or Lifetime account may hold (`cloud_project_limit`);
   * read it through `cloudProjectLimit()`, which supplies the default of 5. */
  cloudProjectLimit?: number;
  /** The included AI allowances from `ai_plan_allowances`, as billing-config
   * reports them; read the lifetime one through `lifetimeIncludedCredits()`. */
  includedCredits?: Partial<IncludedCredits>;
};

export type IncludedCredits = { free: number; lifetime: number; subscriber: number; pro: number };

/** The cap the server applies when `app_runtime_config.cloud_project_limit`
 * is missing; the client quotes it only until the real config arrives. */
export const DEFAULT_CLOUD_PROJECT_LIMIT = LIFETIME_CLOUD_PROJECT_LIMIT;

export { LIFETIME_INCLUDED_CREDITS };

/** The credits a Lifetime buyer gets, as the server reports them; the seed
 * until the config arrives. Never a grant: the database grants. */
export function lifetimeIncludedCredits(config: BillingConfig = useBillingStore.getState().config): number {
  const credits = config.includedCredits?.lifetime;
  return typeof credits === "number" && Number.isInteger(credits) && credits >= 0 ? credits : LIFETIME_INCLUDED_CREDITS;
}

/** The Lifetime cloud-project cap to quote. The server owns the number; this
 * only defaults it and refuses to print a nonsensical one. */
export function cloudProjectLimit(config: BillingConfig = useBillingStore.getState().config): number {
  const limit = config.cloudProjectLimit;
  return typeof limit === "number" && Number.isInteger(limit) && limit >= 1 ? limit : DEFAULT_CLOUD_PROJECT_LIMIT;
}

/** Keeps a malformed `billing-config` field from reaching the UI: the product
 * name must be a non-empty string and the cap a positive integer, or the
 * client falls back to its defaults rather than quoting "undefined" or
 * "0 of NaN cloud projects". Every other field is passed through as before. */
export function sanitizeBillingConfig(data: BillingConfig): BillingConfig {
  const config: BillingConfig = { ...DEFAULT_CONFIG, ...data };
  if (typeof config.productName !== "string" || !config.productName.trim()) config.productName = DEFAULT_CONFIG.productName;
  const limit = config.cloudProjectLimit;
  if (limit !== undefined && !(typeof limit === "number" && Number.isInteger(limit) && limit >= 1)) delete config.cloudProjectLimit;
  const included = config.includedCredits;
  if (included !== undefined) {
    if (typeof included !== "object" || included === null) delete config.includedCredits;
    else {
      const clean: Partial<IncludedCredits> = {};
      for (const plan of ["free", "lifetime", "subscriber", "pro"] as const) {
        const value = (included as Record<string, unknown>)[plan];
        if (typeof value === "number" && Number.isInteger(value) && value >= 0) clean[plan] = value;
      }
      config.includedCredits = clean;
    }
  }
  return config;
}

type BillingState = {
  config: BillingConfig;
  configReady: boolean;
  configUnavailable: boolean;
  entitlement: EntitlementSnapshot | null;
  entitlementSource: "server" | "offline" | "none";
  loading: boolean;
  installationPacks: { userId: string; packs: InstallationPack[] } | null;
  installationPacksSource: "server" | "none";
  subscription: Subscription | null;
  subscriptionSource: "server" | "none";
  subscriptionUserId: string | null;
  subscriptionRequest: { userId: string; status: "loading" | "ready" | "unavailable" } | null;
  loadConfig(): Promise<void>;
  refreshEntitlements(userId: string): Promise<void>;
  refreshInstallationPacks(userId: string): Promise<void>;
  refreshSubscription(userId: string): Promise<void>;
  clear(): void;
};

const DEFAULT_CONFIG: BillingConfig = {
  enabled: false,
  environment: "sandbox",
  priceId: null,
  displayPrice: null,
  productName: "Crimp Lifetime",
};

let entitlementRequestGeneration = 0;
let installationPacksRequestGeneration = 0;
let subscriptionRequestGeneration = 0;

export const useBillingStore = create<BillingState>((set, get) => ({
  config: DEFAULT_CONFIG,
  configReady: false,
  configUnavailable: false,
  entitlement: null,
  entitlementSource: "none",
  loading: false,
  installationPacks: null,
  installationPacksSource: "none",
  subscription: null,
  subscriptionSource: "none",
  subscriptionUserId: null,
  subscriptionRequest: null,

  loadConfig: async () => {
    if (!isSupabaseConfigured) {
      set({
        configReady: true,
        configUnavailable: import.meta.env.PROD,
      });
      return;
    }
    try {
      const data = await requestBillingConfig();
      if (
        !data ||
        typeof data.enabled !== "boolean" ||
        (data.environment !== "sandbox" && data.environment !== "production")
      ) {
        throw new Error("Billing configuration response is invalid");
      }
      set({
        config: sanitizeBillingConfig(data),
        configReady: true,
        configUnavailable: false,
      });
    } catch {
      // A legitimate pre-activation backend returns `enabled: false`. A
      // missing/unreachable configuration is different: fail closed so a
      // startup race or outage cannot grant client-side export access.
      set({
        config: DEFAULT_CONFIG,
        configReady: true,
        configUnavailable: true,
      });
    }
  },

  refreshEntitlements: async (userId) => {
    if (useAuthStore.getState().user?.id !== userId) return;
    const requestGeneration = ++entitlementRequestGeneration;
    const isCurrentRequest = () =>
      requestGeneration === entitlementRequestGeneration &&
      useAuthStore.getState().user?.id === userId;
    set({ loading: true });
    if (isSupabaseConfigured) {
      try {
        const { data, error } = await getSupabase().rpc("get_my_entitlements");
        if (error) throw error;
        if (!isCurrentRequest()) return;
        const rows = (data ?? []) as { kind: "lifetime" | "legacy_project" | "pass"; project_id: string | null; expires_at?: string | null; retained_bom_json_access?: boolean }[];
        const { cacheEntitlements } = await import("../projects/entitlement-cache");
        if (!isCurrentRequest()) return;
        // The server returns lapsed passes for 30 days so the renew window can
        // render; access itself is decided from the date, never from the row.
        const passExpiresAt = rows
          .filter((row) => row.kind === "pass" && typeof row.expires_at === "string" && Number.isFinite(Date.parse(row.expires_at)))
          .map((row) => row.expires_at as string)
          .sort((a, b) => Date.parse(b) - Date.parse(a))[0] ?? null;
        const snapshot = await cacheEntitlements({
          userId,
          lifetime: rows.some((row) => row.kind === "lifetime"),
          retainedBomJsonAccess: rows.some((row) => row.kind === "lifetime" && row.retained_bom_json_access === true),
          legacyProjectIds: rows
            .filter((row) => row.kind === "legacy_project" && row.project_id)
            .map((row) => row.project_id!),
          passExpiresAt,
        });
        if (!isCurrentRequest()) return;
        set({ entitlement: snapshot, entitlementSource: "server", loading: false });
        await Promise.all([get().refreshInstallationPacks(userId), get().refreshSubscription(userId)]);
        return;
      } catch {
        // Fall through to the bounded offline cache.
      }
    }
    if (!isCurrentRequest()) return;
    let cached: EntitlementSnapshot | null = null;
    try {
      const { getValidCachedEntitlements } = await import("../projects/entitlement-cache");
      if (!isCurrentRequest()) return;
      cached = await getValidCachedEntitlements(userId);
    } catch {
      // A failed optional cache/chunk must not create paid rights or leave
      // loading stuck. Only current server responses can grant access.
    }
    if (!isCurrentRequest()) return;
    set({
      entitlement: cached,
      entitlementSource: cached ? "offline" : "none",
      loading: false,
    });
    await get().refreshSubscription(userId);
  },

  refreshInstallationPacks: async (userId) => {
    const generation = entitlementRequestGeneration;
    const packsGeneration = ++installationPacksRequestGeneration;
    const current = () => generation === entitlementRequestGeneration &&
      packsGeneration === installationPacksRequestGeneration && useAuthStore.getState().user?.id === userId;
    if (!current()) return;
    try {
      const { data, error } = await getSupabase().rpc("get_my_installation_packs");
      if (error || !Array.isArray(data)) throw new Error("Installation rights unavailable");
      const packs: InstallationPack[] = data.flatMap((row: Record<string, unknown>) =>
        typeof row.installation_id === "string" && typeof row.name === "string" &&
        Array.isArray(row.client_project_ids) && row.client_project_ids.every((id) => typeof id === "string")
          ? [{ installationId: row.installation_id, name: row.name,
              cloudProjectId: typeof row.cloud_project_id === "string" ? row.cloud_project_id : null,
              clientProjectIds: row.client_project_ids as string[] }]
          : []);
      if (current()) set({ installationPacks: { userId, packs }, installationPacksSource: "server" });
    } catch {
      if (current()) set({ installationPacks: null, installationPacksSource: "none" });
    }
  },

  refreshSubscription: async (userId) => {
    const generation = entitlementRequestGeneration;
    const request = ++subscriptionRequestGeneration;
    const current = () => generation === entitlementRequestGeneration && request === subscriptionRequestGeneration &&
      useAuthStore.getState().user?.id === userId;
    if (!current()) return;
    set({ subscriptionRequest: { userId, status: "loading" } });
    try {
      const { data, error } = await getSupabase().rpc("get_my_subscription");
      if (error || !Array.isArray(data)) throw new Error("Subscription unavailable");
      const row = data[0];
      if (row && (typeof row.subscription_id !== "string" ||
          !["trialing", "active", "past_due", "paused", "canceled"].includes(row.status) ||
          typeof row.active !== "boolean")) throw new Error("Invalid subscription");
      const date = (value: unknown) => typeof value === "string" && Number.isFinite(Date.parse(value)) ? value : null;
      const subscription: Subscription | null = row ? {
        userId, subscriptionId: row.subscription_id, status: row.status, active: row.active,
        trialEndsAt: date(row.trial_ends_at), currentPeriodEndsAt: date(row.current_period_ends_at),
        nextBilledAt: date(row.next_billed_at), scheduledCancelAt: date(row.scheduled_cancel_at),
        productKey: ["pro_monthly", "pro_yearly"].includes(row.product_key) ? row.product_key : "monthly_subscription",
        billingInterval: row.billing_interval === "year" ? "year" : "month",
      } : null;
      if (current()) set({ subscription, subscriptionSource: "server", subscriptionUserId: userId,
        subscriptionRequest: { userId, status: "ready" } });
    } catch {
      // A local cache or a prior account must never extend subscription access.
      if (current()) set({ subscription: null, subscriptionSource: "none", subscriptionUserId: null,
        subscriptionRequest: { userId, status: "unavailable" } });
    }
  },

  clear: () => {
    entitlementRequestGeneration += 1;
    set({ entitlement: null, entitlementSource: "none", loading: false,
      installationPacks: null, installationPacksSource: "none", subscription: null, subscriptionSource: "none", subscriptionUserId: null, subscriptionRequest: null });
  },
}));

export function hasPaidAccess(_projectId?: string | null): boolean {
  const {
    config,
    configReady,
    configUnavailable,
    entitlement,
    entitlementSource,
  } =
    useBillingStore.getState();
  if (!configReady) return false;
  // IndexedDB is fully user-controlled and therefore only a UX cache. It
  // must never create paid access by itself; only the current authenticated
  // server response is authoritative. A signed server artefact could support
  // offline paid actions in the future without weakening this boundary.
  const hasVerifiedEntitlement =
    (entitlementSource === "server" && !!entitlement &&
      entitlement.userId === useAuthStore.getState().user?.id &&
      (entitlement.lifetime === true || passActiveAt(entitlement, Date.now()))) || hasSubscriptionAccess();
  if (configUnavailable) return hasVerifiedEntitlement;
  if (!config.enabled) return true;
  return hasVerifiedEntitlement;
}

/** How long a lapsed pass is still shown as "ended" with a renew action. */
export const PASS_RENEW_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

function passActiveAt(snapshot: EntitlementSnapshot | null | undefined, now: number): boolean {
  return !!snapshot?.passExpiresAt && Date.parse(snapshot.passExpiresAt) > now;
}

/** Server-confirmed pass state for the signed-in account. A pass that ended
 * within the renew window reads "expired"; older or absent passes read "none".
 * Like every other access read, an offline cache never counts. */
export function passAccess(now = Date.now()): "none" | "active" | "expired" {
  const { entitlement, entitlementSource } = useBillingStore.getState();
  if (entitlementSource !== "server" || !entitlement || entitlement.userId !== useAuthStore.getState().user?.id || !entitlement.passExpiresAt) return "none";
  const ends = Date.parse(entitlement.passExpiresAt);
  if (!Number.isFinite(ends)) return "none";
  if (ends > now) return "active";
  return now - ends < PASS_RENEW_WINDOW_MS ? "expired" : "none";
}

/** Whether the signed-in account holds a server-confirmed purchase that the
 * cloud-project cap applies to: a lifetime licence or a running Pass. Unlike
 * `hasPaidAccess`, a disabled-billing build and a subscription both read
 * false — subscribers are uncapped and a free build has nothing to count. */
export function verifiedPurchasedAccess(now = Date.now()): boolean {
  const { entitlement, entitlementSource } = useBillingStore.getState();
  if (entitlementSource !== "server" || !entitlement || entitlement.userId !== useAuthStore.getState().user?.id) return false;
  return entitlement.lifetime === true || passActiveAt(entitlement, now);
}

/** ISO end of the signed-in account's latest server-confirmed pass, or null. */
export function passExpiresAt(): string | null {
  const { entitlement, entitlementSource } = useBillingStore.getState();
  if (entitlementSource !== "server" || !entitlement || entitlement.userId !== useAuthStore.getState().user?.id) return null;
  return entitlement.passExpiresAt ?? null;
}

/** Server-confirmed access also expires locally, even if the editor stays open. */
export function hasSubscriptionAccess(now = Date.now()): boolean {
  const { subscription: subscription, subscriptionSource } = useBillingStore.getState();
  return subscriptionSource === "server" && !!subscription &&
    subscription.userId === useAuthStore.getState().user?.id && subscription.active &&
    ["active", "canceled"].includes(subscription.status) &&
    !!subscription.currentPeriodEndsAt && Date.parse(subscription.currentPeriodEndsAt) > now;
}

/** Professional features require a current server-confirmed Pro subscription. */
export function hasProAccess(now = Date.now()): boolean {
  const product = useBillingStore.getState().subscription?.productKey;
  return (product === "pro_monthly" || product === "pro_yearly") && hasSubscriptionAccess(now);
}

/** BoM and JSON are Professional features; pre-rollout Lifetime rights remain. */
export function hasBomJsonAccess(now = Date.now()): boolean {
  if (hasProAccess(now)) return true;
  const { entitlement, entitlementSource } = useBillingStore.getState();
  return entitlementSource === "server" &&
    entitlement?.userId === useAuthStore.getState().user?.id &&
    entitlement?.lifetime === true && entitlement.retainedBomJsonAccess === true;
}

/** Pack rights belong only to a server-confirmed installation owned by this
 * account. They never turn hasPaidAccess into account-wide Pro. */
export function currentInstallationPack(localProjectId = useProjectStore.getState().activeLocalProjectId): InstallationPack | null {
  const auth = useAuthStore.getState();
  const billing = useBillingStore.getState();
  if (!billing.configReady || billing.installationPacksSource !== "server" ||
      billing.installationPacks?.userId !== auth.user?.id || !localProjectId) return null;
  return billing.installationPacks?.packs.find((pack) => pack.clientProjectIds.includes(localProjectId) ||
    (localProjectId === useProjectStore.getState().activeLocalProjectId &&
      auth.currentOwnerId === auth.user?.id && auth.currentProjectId !== null && pack.cloudProjectId === auth.currentProjectId)) ?? null;
}

export function hasExportAccess(localProjectId = useProjectStore.getState().activeLocalProjectId): boolean {
  return hasPaidAccess() || currentInstallationPack(localProjectId) !== null;
}
